import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { updateBankGuarantee } from "../api/bank_guarantee"

const toEuros = cents => (cents == null ? "" : (cents / 100).toString().replace(".", ","))
const toEurosFloat = value => {
  if (value == null || value === "") return 0
  const normalized = String(value).replace(/\s/g, "").replace(",", ".")
  const n = parseFloat(normalized)
  return isNaN(n) ? 0 : n
}

const BankGuaranteeModal = ({ isOpen, projectId, bankGuarantee, onClose }) => {
  const [enabled,     setEnabled]     = useState(false)
  const [bankName,    setBankName]    = useState("")
  const [reference,   setReference]   = useState("")
  const [amount,      setAmount]      = useState("")
  const [expiresOn,   setExpiresOn]   = useState("")
  const [saving,      setSaving]      = useState(false)
  const [errors,      setErrors]      = useState([])

  useEffect(() => {
    if (!isOpen) return
    setEnabled(!!bankGuarantee?.enabled)
    setBankName(bankGuarantee?.bank_name  || "")
    setReference(bankGuarantee?.reference || "")
    setAmount(toEuros(bankGuarantee?.amount?.cents))
    setExpiresOn(bankGuarantee?.expires_on || "")
    setErrors([])
  }, [isOpen, bankGuarantee])

  const handleSave = () => {
    if (saving) return
    setSaving(true)
    setErrors([])

    updateBankGuarantee(projectId, {
      bank_guarantee_enabled:    enabled,
      bank_guarantee_bank_name:  bankName,
      bank_guarantee_reference:  reference,
      bank_guarantee_expires_on: expiresOn || null,
      amount_euros:              toEurosFloat(amount),
    })
      .then(response => {
        if (response?.errors) {
          setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
          return
        }
        window.location.reload()
      })
      .catch(() => setErrors([I18n.t("projects.bank_guarantee.errors.network")]))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost"   onClick={onClose}>{I18n.t("projects.bank_guarantee.form.cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {I18n.t("projects.bank_guarantee.form.save")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {I18n.t("projects.bank_guarantee.form.eyebrow")}
      title   = {I18n.t("projects.bank_guarantee.form.title")}
      footer  = {footer}
    >
      <p className="text-[12.5px] text-slate-600 mb-4">
        {I18n.t("projects.bank_guarantee.form.intro")}
      </p>

      <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
        <input
          type      = "checkbox"
          checked   = {enabled}
          onChange  = {e => setEnabled(e.target.checked)}
          className = "h-4 w-4 rounded border-slate-300"
        />
        <span className="text-sm font-medium text-slate-900">
          {I18n.t("projects.bank_guarantee.form.toggle_label")}
        </span>
      </label>

      <fieldset disabled={!enabled} className={!enabled ? "opacity-50" : ""}>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.bank_guarantee.form.bank_name")}
            </label>
            <input
              value     = {bankName}
              onChange  = {e => setBankName(e.target.value)}
              placeholder = {I18n.t("projects.bank_guarantee.form.bank_name_placeholder")}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
            />
          </div>

          <div className="col-span-5">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.bank_guarantee.form.reference")}
            </label>
            <input
              value     = {reference}
              onChange  = {e => setReference(e.target.value)}
              placeholder = {I18n.t("projects.bank_guarantee.form.reference_placeholder")}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm font-mono"
            />
          </div>

          <div className="col-span-7">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.bank_guarantee.form.amount")}
            </label>
            <div className="relative">
              <input
                value     = {amount}
                onChange  = {e => setAmount(e.target.value)}
                placeholder = "0,00"
                className = "w-full h-10 px-3 pr-10 rounded-md border border-slate-200 text-sm text-right tabular-nums"
              />
              <span className="absolute inset-y-0 right-0 pr-3 inline-flex items-center text-slate-500 text-sm pointer-events-none">€</span>
            </div>
          </div>

          <div className="col-span-5">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.bank_guarantee.form.expires_on")}
            </label>
            <input
              type      = "date"
              value     = {expiresOn || ""}
              onChange  = {e => setExpiresOn(e.target.value)}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
            />
          </div>
        </div>
      </fieldset>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default BankGuaranteeModal
